import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  LayoutGrid,
  List,
  FolderTree,
  Archive,
  Briefcase,
  Users,
  UserCog,
  FileText,
  Inbox,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const sectionStyle: React.CSSProperties = {
  marginBottom: '8px',
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--color-sidebar-text-muted)',
  marginTop: '24px',
  marginBottom: '8px',
  paddingLeft: '16px',
};

const baseLinkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '9px 16px',
  margin: '0 8px',
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--color-sidebar-text)',
  borderRadius: 'var(--radius)',
  textDecoration: 'none',
  transition: 'background 100ms ease, color 100ms ease',
};

const activeLinkStyle: React.CSSProperties = {
  ...baseLinkStyle,
  color: '#fff',
  background: 'var(--color-coral)',
  fontWeight: 600,
};

function NavItem({ to, label, Icon, end }: { to: string; label: string; Icon: LucideIcon; end?: boolean }) {
  const [hovered, setHovered] = useState(false);
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => {
        if (isActive) return activeLinkStyle;
        return hovered
          ? { ...baseLinkStyle, background: 'var(--color-navy-deep-hover)' }
          : baseLinkStyle;
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Icon size={16} strokeWidth={2} />
      {label}
    </NavLink>
  );
}

const avatarPalette = ['var(--color-coral)', 'var(--color-blue-accent)', 'var(--color-green)'];

function hashName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Sidebar() {
  const { auth } = useAuth();
  const name = auth.status === 'authenticated' ? auth.name : null;
  const email = auth.status === 'authenticated' ? auth.email : null;
  const avatarBg = name ? avatarPalette[hashName(name) % avatarPalette.length] : avatarPalette[0];

  return (
    <aside
      style={{
        width: 'var(--sidebar-width)',
        flexShrink: 0,
        backgroundColor: 'var(--color-navy-deep)',
        padding: '16px 0',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <nav style={{ flex: 1 }}>
        <div style={{ ...sectionStyle, marginTop: '8px' }}>
          <NavItem to="/dashboard" label="Home" Icon={Home} end />
        </div>
        <div style={sectionStyle}>
          <div style={sectionLabelStyle}>Tasks</div>
          <NavItem to="/tasks/board" label="Board" Icon={LayoutGrid} />
          <NavItem to="/tasks/list" label="List" Icon={List} />
        </div>
        <div style={sectionStyle}>
          <div style={sectionLabelStyle}>Projects</div>
          <NavItem to="/projects" label="Theme tree" Icon={FolderTree} end />
          <NavItem to="/projects/archive" label="Archive" Icon={Archive} />
        </div>
        <div style={sectionStyle}>
          <div style={sectionLabelStyle}>CRM</div>
          <NavItem to="/crm" label="Pipeline" Icon={Briefcase} end />
          <NavItem to="/crm/archive" label="Archive" Icon={Archive} />
        </div>
        <div style={sectionStyle}>
          <div style={sectionLabelStyle}>People</div>
          <NavItem to="/people/founders" label="Founders" Icon={Users} end />
          <NavItem to="/people/external" label="External" Icon={UserCog} />
        </div>
        <div style={sectionStyle}>
          <div style={sectionLabelStyle}>Transcripts</div>
          <NavItem to="/transcripts/import" label="Import" Icon={FileText} />
          <NavItem to="/transcripts/review" label="Review inbox" Icon={Inbox} />
        </div>
      </nav>

      {name && (
        <div
          style={{
            marginTop: '16px',
            padding: '16px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div className="avatar" style={{ background: avatarBg }}>
            {initialsOf(name)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#fff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {name}
            </div>
            {email && (
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--color-sidebar-text-muted)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {email}
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
